// FinalCustomersTargeted.jsx — BespokeBridge
// Shows the final contact list after applying all exclusion rules.

import React, { useState } from 'react';
import ExclusionPreviewModal from './ExclusionPreviewModal';
import {
  getContactsAfterUnsubscribeExclusion,
  getContactsAfterFrequencyExclusion,
  getContactsAfterRecurrenceExclusion,
  getFinalContactList,
} from '../../../api/campaignExclusionService';

const CAMPAIGN_TYPE_NAMES = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  push: 'Push Notification',
};

// Derive the audience type ('members'/'volunteers'/'donors'/'both'/'all') from either
// formData.audienceType (EmailCampaigns) or formData.targetingType (WhatsApp/Push).
function deriveAudienceType(formData, campaignType) {
  if (formData.audienceType) return formData.audienceType;
  const t = formData.targetingType || '';
  if (['all_members', 'member_status', 'member_birthday', 'member_district'].includes(t)) return 'members';
  if (['all_volunteers', 'volunteer_district', 'volunteer_status'].includes(t)) return 'volunteers';
  if (t === 'all_donors') return 'donors';
  if (t === 'all_contacts') return campaignType === 'email' ? 'all' : 'both'; // donors are email-only
  return 'both'; // specific_contacts or fallback
}

function FinalCustomersTargeted({ orgId, campaignType, formData, disabled = false }) {
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [modalType, setModalType]   = useState('');
  const [previewData, setPreviewData] = useState(null);

  const displayName = CAMPAIGN_TYPE_NAMES[campaignType] || campaignType;

  const _targetingType   = formData.targetingType || '';
  const _selectedContacts = formData.selectedContacts || [];
  const _rawFilters       = formData.audienceFilters || {};

  // When specific contacts are selected, inject their IDs as the filter so every
  // exclusion step (unsubscribe / frequency / recurrence) sees the correct base set.
  const resolvedAudienceFilters =
    _targetingType === 'specific_contacts'
      ? { specificContactIds: _selectedContacts.map(c => c.id) }
      : _rawFilters;

  const targetingOptions = {
    audienceType:     deriveAudienceType(formData, campaignType),
    targetingType:    _targetingType || 'all_contacts',
    audienceFilters:  resolvedAudienceFilters,
    selectedContacts: _selectedContacts,
  };

  const recurringSettings =
    (formData.scheduleType === 'recurring' || formData.schedulingType === 'recurring')
      ? formData.recurring
      : null;

  const isRecurring = !!recurringSettings?.excludePreviousRecipients;

  const handlePreview = async (type) => {
    if (!orgId) {
      setError('Organisation ID is required');
      setTimeout(() => setError(''), 3000);
      return;
    }
    try {
      setLoading(true);
      setError('');
      let data;
      switch (type) {
        case 'unsubscribe':
          data = await getContactsAfterUnsubscribeExclusion(orgId, campaignType, targetingOptions);
          break;
        case 'frequency':
          data = await getContactsAfterFrequencyExclusion(orgId, campaignType, targetingOptions);
          break;
        case 'recurrence':
          data = await getContactsAfterRecurrenceExclusion(orgId, campaignType, targetingOptions, recurringSettings);
          break;
        case 'final':
          data = await getFinalContactList(orgId, campaignType, targetingOptions, recurringSettings);
          break;
        default:
          throw new Error('Invalid preview type');
      }
      setPreviewData(data);
      setModalType(type);
      setShowModal(true);
    } catch (err) {
      console.error(`Error getting ${type} preview:`, err);
      setError(`Failed to load preview: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-comm-section-card">
      <div className="bb-comm-card-title">Final Contacts Targeted</div>
      <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
        Preview the final list of contacts who will receive this {displayName.toLowerCase()} campaign
        after applying all exclusion rules (frequency limits and recurring campaign settings).
      </p>

      {disabled && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400E' }}>
          <strong>Save targeting criteria first.</strong> Click "Save Targeting Criteria" in the Audience section above to lock in your segment before previewing the final contact list.
        </div>
      )}

      <div style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>

      {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Button 1: Unsubscribe Exclusion */}
        <button
          type="button"
          className="bb-comm-exclusion-preview-btn"
          onClick={() => handlePreview('unsubscribe')}
          disabled={disabled || loading}
        >
          <span style={{ fontSize: 18 }}>✉️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Preview List After Excluding {displayName} Unsubscribes
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
              Shows contacts eligible after removing those who have unsubscribed
            </div>
          </div>
          <span style={{ color: '#9CA3AF' }}>→</span>
        </button>

        {/* Button 2: Frequency Exclusion */}
        <button
          type="button"
          className="bb-comm-exclusion-preview-btn"
          onClick={() => handlePreview('frequency')}
          disabled={disabled || loading}
        >
          <span style={{ fontSize: 18 }}>🚫</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Preview List After Excluding Frequency Limits
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
              Shows contacts eligible after removing those who reached frequency caps
            </div>
          </div>
          <span style={{ color: '#9CA3AF' }}>→</span>
        </button>

        {/* Button 3: Recurrence Exclusion (recurring campaigns only) */}
        {isRecurring && (
          <button
            type="button"
            className="bb-comm-exclusion-preview-btn"
            onClick={() => handlePreview('recurrence')}
            disabled={disabled || loading}
          >
            <span style={{ fontSize: 18 }}>🔄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Preview List After Excluding Recipients from Earlier Runs
              </div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                Shows contacts eligible after removing previous campaign recipients
              </div>
            </div>
            <span style={{ color: '#9CA3AF' }}>→</span>
          </button>
        )}

        {/* Button 4: Final Nett List */}
        <button
          type="button"
          className="bb-comm-exclusion-preview-btn bb-comm-exclusion-preview-btn--final"
          onClick={() => handlePreview('final')}
          disabled={disabled || loading}
        >
          <span style={{ fontSize: 18 }}>🎯</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Final Nett List After All Exclusions
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
              Final list of contacts who will receive the campaign (used for Actual Blast)
            </div>
          </div>
          <span style={{ color: '#10B981', fontWeight: 700 }}>✓</span>
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '12px 0', color: '#6B7280', fontSize: 13 }}>
          Loading preview…
        </div>
      )}

      <div style={{ marginTop: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1D4ED8' }}>
        <strong>Important:</strong> The "Final Nett List" represents the exact contacts who will receive
        your campaign when you click "Send Actual Blast". This list excludes: (i) contacts who have
        unsubscribed from {displayName.toLowerCase()} communications, (ii) contacts who have reached
        frequency limits, and (iii) recipients of previous campaign runs (for recurring campaigns).
      </div>

      <ExclusionPreviewModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        previewData={previewData}
        modalType={modalType}
        campaignName={formData.name || formData.subject || `${displayName} campaign`}
      />
      </div>
    </div>
  );
}

export default FinalCustomersTargeted;
